/**
 * Infrastructure Help Content
 * 
 * Feature: 136-dashboard-alignment (T055)
 * Pages: /infrastructure, /infrastructure/health, /monitoring/health, /agents
 * Constitution: v1.12.0
 */

import type { PageHelpContent } from '../../components/ui/PageHelpPanel';

export const infrastructureHelp: PageHelpContent = {
  pageTitle: 'Infrastructure & Monitoring',
  purpose: 'Monitor infrastructure health, agent status, and system-level metrics for the Fabric Control Plane.',
  sections: [
    {
      title: 'Infrastructure Inventory',
      paragraphs: [
        'View all Azure resources that support the Fabric Control Plane: App Services, Container Instances, Key Vaults, Storage Accounts, and SQL Databases.',
      ],
    },
    {
      title: 'Health Monitoring',
      concepts: [
        { term: 'Component Health', definition: 'Individual infrastructure component status: API server, database, Key Vault, storage, collection agents.' },
        { term: 'Health Score', definition: 'Composite score (0-100) based on component availability, response times, and error rates.' },
        { term: 'Health Trend', definition: 'Historical health score plotted over time. Identifies degradation patterns before they become incidents.' },
      ],
    },
    {
      title: 'Collection Agents',
      paragraphs: [
        'Agents are containerized processes that run data collection, backup, and maintenance jobs. Monitor agent status, last heartbeat, and job assignment.',
      ],
      concepts: [
        { term: 'Agent Status', definition: 'Active (running jobs), Idle (waiting for work), Offline (no heartbeat), or Error (failed to start).' },
        { term: 'Heartbeat', definition: 'Periodic signal from an agent confirming it is alive. Missing heartbeats trigger alerts.' },
      ],
    },
    {
      title: 'How To',
      steps: [
        { step: 1, instruction: 'Open the Infrastructure page to view the health dashboard showing the composite health score and component statuses.' },
        { step: 2, instruction: 'Review individual component status cards for API server, database, Key Vault, storage, and collection agents.' },
        { step: 3, instruction: 'Check the Health Trend chart to identify degradation patterns or recurring issues over time.' },
        { step: 4, instruction: 'Navigate to Collection Agents to verify agent heartbeats, job assignments, and detect any offline agents.' },
      ],
    },
    {
      title: 'Related Pages',
      links: [
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Alerts', href: '/alerts' },
        { label: 'Deployments', href: '/deployments' },
      ],
    },
  ],
};

export default infrastructureHelp;
